import { Divider, Stack } from '@mui/material';
import { useCallback, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LassieParams, LassieWorkstation } from '@thive/fddm-types';
import { degToRad, radToDeg } from 'three/src/math/MathUtils.js';
import { useShallow } from 'zustand/shallow';

import { Accordion } from '@common/components/Accordion';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { WorkspaceMode } from '@/modules/common/types/general';
import {
  getMultiSelectDisplayValue,
  MultiSelectInput,
  NumberInput,
} from '@/modules/common/components/inputs';
import { NumUtils } from '@/modules/common/utils';
import { computeRectanglesBoundingBoxExtent } from '@/modules/common/helpers/boundingBox';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { LoadingServiceFactory } from '@/modules/app/service/LoadingServiceFactory';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { useReadSelectedTemplates } from '@/modules/floorplanDesign/hooks';
import {
  areasToFlatCollection,
  hasNoTemplates,
  isHighwayArea,
} from '@/modules/floorplanDesign/helpers';
import { RectangleUtils } from '@/modules/floorplanDesign/helpers/RectangleUtils';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';

import { TpsServiceFactory } from '../service/TpsServiceFactory';
import { WorkstationConfigs } from './workstation';
import { TemplateDimensions } from './TemplateDimensions';

function LassieTemplateParametersComponent() {
  const { t } = useTranslation();
  const mode = useAppStore((state) => state.mode);
  const loadingSvc = useMemo(() => LoadingServiceFactory.getInstance().createLoadingService(), []);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const tpsSvc = useMemo(() => TpsServiceFactory.getInstance().createTpsService(), []);
  const selectedGroups = useReadSelectedTemplates();

  const lassieGroup = useMemo(() => {
    if (hasNoTemplates(selectedGroups)) return;

    const group = selectedGroups.lassies[0];
    if (!group) return;

    return group;
  }, [selectedGroups]);

  const newGenerationParams = useMemo(() => {
    if (!lassieGroup) return;

    return lassieGroup.params;
  }, [lassieGroup]);

  const sprinklerOptions = useMemo(() => {
    if (!newGenerationParams) return [];

    return Array.from(Array(newGenerationParams.numShelves - 1), (el, i) => i + 1).map((val) => ({
      label: t('interface:properties.template.sprinklers.option_label', {
        value: val,
      }),
      value: String(val),
    }));
  }, [newGenerationParams, t]);

  const sprinklersDisplayValue = useMemo(() => {
    if (!newGenerationParams) return '';

    return getMultiSelectDisplayValue(
      newGenerationParams.sprinklerShelves.map((val) => ({
        name: String(val),
        id: String(val),
      })),
      newGenerationParams.sprinklerShelves.map((item) => String(item)),
    );
  }, [newGenerationParams]);

  const dimensions = useFloorplanDesignStore(
    useShallow((state) => {
      if (!lassieGroup) return;

      const areasInGroupIds = lassieGroup.group.areaIds;
      const areasInGroups = areasToFlatCollection(state.getAreasByIds(areasInGroupIds));

      const rectangles = areasInGroups.flatMap((item) =>
        isHighwayArea(item) ? item.segments.flatMap(RectangleUtils.fromSegment) : [item.rectangle],
      );

      if (rectangles.length === 0) return null;

      const box = computeRectanglesBoundingBoxExtent(rectangles);
      return { x: box.x, y: box.y, length: box.width, width: box.height };
    }),
  );

  const regenerate = useCallback(
    async (params?: Partial<LassieParams>) => {
      if (!dimensions || !newGenerationParams || !lassieGroup) return;

      loadingSvc.showLoadContainer('TRANSPARENT');

      await tpsSvc.regenerateLassieTemplate({
        ...lassieGroup,
        params: { ...newGenerationParams, ...params },
      });

      loadingSvc.hideLoadContainer();
    },
    [dimensions, newGenerationParams, lassieGroup, tpsSvc, loadingSvc],
  );

  const onNumAislesChange = useCallback(
    (newValue: number) => {
      fddmSvc.setPreference('template.lassie.numAisles', newValue);
      regenerate({ numAisles: newValue });
    },
    [fddmSvc, regenerate],
  );

  const onNumBaysChange = useCallback(
    (newValue: number) => {
      fddmSvc.setPreference('template.lassie.numBays', newValue);
      regenerate({ numBays: newValue });
    },
    [fddmSvc, regenerate],
  );

  const onNumShelvesChange = useCallback(
    (newValue: number) => {
      fddmSvc.setPreference('template.lassie.numShelves', newValue);
      regenerate({ numShelves: newValue });
    },
    [fddmSvc, regenerate],
  );

  const onSprinklerOptionClick = useCallback(
    (value: string) => {
      if (!newGenerationParams) return;

      const newValue = Number(value);
      const { numShelves, sprinklerShelves } = newGenerationParams;
      let updated = [...sprinklerShelves];
      updated = updated.filter((sprinklerLevel) => sprinklerLevel <= numShelves);
      updated = updated.includes(newValue)
        ? updated.filter((item) => item !== newValue)
        : [...updated, newValue];
      updated.sort((a, b) => a - b);

      regenerate({ sprinklerShelves: updated });
    },
    [newGenerationParams, regenerate],
  );

  const onConfigUpdate = useCallback(
    (newValue: LassieWorkstation[]) => {
      regenerate({ workstations: newValue });
    },
    [regenerate],
  );

  const onAddWorkstationConfigClick = useCallback(() => {
    if (!newGenerationParams) return;

    const { workstations } = newGenerationParams;
    const newWorkstations: LassieWorkstation = {
      type: 'SINGLE_RAMP_TYPE_A',
      positionFromBottom: 0,
    };

    regenerate({ workstations: [...workstations, newWorkstations] });
  }, [newGenerationParams, regenerate]);

  const onCenterXChange = useCallback(
    (newValue: number) => {
      regenerate({ centerX: NumUtils.mmToMeters(newValue) });
    },
    [regenerate],
  );

  const onCenterYChange = useCallback(
    (newValue: number) => {
      regenerate({ centerY: NumUtils.mmToMeters(newValue) });
    },
    [regenerate],
  );

  const onAngleChange = useCallback(
    (newValue: number) => {
      regenerate({ angle: degToRad(newValue) });
    },
    [regenerate],
  );

  if (!newGenerationParams || !dimensions) return null;

  return (
    <Stack spacing={1} divider={<Divider />}>
      <TemplateDimensions
        x={newGenerationParams.centerX}
        y={newGenerationParams.centerY}
        length={dimensions.length}
        width={dimensions.width}
        angle={radToDeg(newGenerationParams.angle)}
        onXChange={onCenterXChange}
        onYChange={onCenterYChange}
        onAngleChange={onAngleChange}
      />
      <Accordion
        title={t('interface:properties.template_title.lassie', 'Lassie')}
        unmountOnExit
        defaultExpanded
        sx={{ px: 2 }}
      >
        <Stack direction='column' spacing={1}>
          <PropertiesLabel i18nkey='interface:properties.template.aisles'>
            <NumberInput
              value={newGenerationParams.numAisles}
              onChange={onNumAislesChange}
              disabled={mode !== WorkspaceMode.EDITABLE}
              minValue={1}
            />
          </PropertiesLabel>
          <PropertiesLabel i18nkey='interface:properties.template.bays'>
            <NumberInput
              value={newGenerationParams.numBays}
              onChange={onNumBaysChange}
              disabled={mode !== WorkspaceMode.EDITABLE}
              minValue={1}
            />
          </PropertiesLabel>
          <PropertiesLabel i18nkey='interface:properties.template.shelves'>
            <NumberInput
              value={newGenerationParams.numShelves}
              onChange={onNumShelvesChange}
              minValue={1}
              disabled={mode !== WorkspaceMode.EDITABLE}
            />
          </PropertiesLabel>
          <PropertiesLabel i18nkey='interface:properties.template.sprinklers.input_label'>
            <MultiSelectInput
              options={sprinklerOptions}
              selectedOptions={newGenerationParams.sprinklerShelves.map((item) => String(item))}
              onItemClick={onSprinklerOptionClick}
              displayValue={sprinklersDisplayValue}
              disabled={mode !== WorkspaceMode.EDITABLE}
            />
          </PropertiesLabel>
          <Divider />
          <WorkstationConfigs
            configs={newGenerationParams.workstations}
            onAddClick={onAddWorkstationConfigClick}
            updateConfigs={onConfigUpdate}
          />
        </Stack>
      </Accordion>
    </Stack>
  );
}

export const LassieTemplateParameters = memo(LassieTemplateParametersComponent);
